import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { captureException } from '@sentry/react';
import {
    StockCardComponent,
    StockCardQuery,
    UpdateWarehouseCardMutationVariables,
    useRemoveStockCardMutation,
    useUpdateWarehouseCardMutation,
} from '../../../../generated/graphql';
import { STOCK_CARDS_QUERY } from '../../graphql/queries';
import { useWarehousesScope } from '../../providers/WarehouseScopeProvider';
import { routePaths } from '../../../../routePaths';
import { LocationReferrer } from '../../../../types/locationReferrer';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { useUser } from '../../../../utils/hooks/auth';
import { WarehouseCardFormValues } from './types';
import WarehouseCardForm from './WarehouseCardsForm';
import RemovalConfirmationDialog from './RemovalConfirmationDialog';
import { getPriceUpdateArgs } from './utils';

type StockLevel = StockCardQuery['stockCard']['stockLevels'][0];

const UpdateWarehouseCardForm = () => {
    const { params: { id: warehouseCardId } } = useRouteMatch();
    const { priceService } = usePriceService();
    const { user: { defaultCurrency: { currencyCode } } } = useUser();
    const { currentWarehouseId } = useWarehousesScope();
    const [isRemoveWarehouseDialogOpen, setIsRemoveWarehouseDialogOpen] = useState(false);

    const handleRemoveWarehouseDialogClose = () => setIsRemoveWarehouseDialogOpen(false);
    const handleRemoveWarehouseDialogOpen = () => setIsRemoveWarehouseDialogOpen(true);

    const [updateWarehouseCard] = useUpdateWarehouseCardMutation({
        refetchQueries: [{ query: STOCK_CARDS_QUERY, variables: { warehouseIds: [currentWarehouseId] } } ]
    });
    const [removeWarehouseCard] = useRemoveStockCardMutation({
        refetchQueries: [{ query: STOCK_CARDS_QUERY, variables: { warehouseIds: [currentWarehouseId] } } ]
    });

    const history = useHistory<LocationReferrer>();
    const { state = {} } = history.location;
    const { referrer } = state;
    const handleClose = () => {
        history.push(referrer || routePaths.warehouseCards);
    };

    const onSubmit = async ({
        name,
        warehouseIds,
        categoryId,
        taxId,
        sku,
        priceWithoutVat,
    }: WarehouseCardFormValues) => {
        const priceUpdateArgs = getPriceUpdateArgs({ priceWithoutVat, warehouseIds: [currentWarehouseId as string], priceService });

        const variables: UpdateWarehouseCardMutationVariables = {
            id: warehouseCardId,
            data: {
                name,
                sku,
                taxId,
                warehouseIds,
                stockCardCategoryId: categoryId,
            },
            priceUpdateArgs,
        };

        await updateWarehouseCard({ variables });
    };

    const { t } = useTranslation();
    const buttonText = t('Update');
    const dialogName = t('Update warehouse card');

    const handleWarehouseCardRemove = async (warehouseIds: string[]) => {
        try {
            await removeWarehouseCard({ variables: { id: warehouseCardId, warehouseIds } });
            handleRemoveWarehouseDialogClose();
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };
    return (
        <StockCardComponent
            nextFetchPolicy="cache-first"
            variables={{ id: warehouseCardId, currentVenueOnly: false }}
            redirectOnErrorUrl={routePaths.warehouseCards}
        >
            {({ data: { stockCard } }) => {
            const {
                taxId,
                sku,
                stockCardCategory,
                convertedAmount,
                conversionUnit,
                storageUnit,
                warehouses,
                name,
                stockLevels,
            } = stockCard;

            const warehouseIds = warehouses.map(({ id }) => id) ?? [];
            const currentWarehousetockLevel = stockLevels.find(({ warehouseId }) => warehouseId === currentWarehouseId) as StockLevel;

            let priceWithoutVat = 0;

            if (!currentWarehousetockLevel) {
                captureException('Failed to get currentWarehousetockLevel', { extra: { currentWarehouseId, warehouseCardId } });
            } else {
                priceWithoutVat = priceService.toUnit(currentWarehousetockLevel.priceWithoutVat, currencyCode);
            }

            const initialValues: WarehouseCardFormValues = {
                name,
                taxId,
                warehouseIds,
                categoryId: stockCardCategory?.id,
                sku,
                storageUnitId: storageUnit,
                convertedQuanitity: convertedAmount,
                convertedUnit: conversionUnit,
                priceWithoutVat,
            };

            return (
                <>
                    <WarehouseCardForm
                        isUpdate
                        onClose={handleClose}
                        initialValues={initialValues}
                        buttonText={buttonText}
                        dialogName={dialogName}
                        onSubmit={onSubmit}
                        currentWarehouseId={currentWarehouseId as string}
                        onRemove={handleRemoveWarehouseDialogOpen}
                    />
                    <RemovalConfirmationDialog
                        warehouseCardName={name}
                        warehouses={warehouses}
                        open={isRemoveWarehouseDialogOpen}
                        handleClose={handleRemoveWarehouseDialogClose}
                        handleStockCardRemove={handleWarehouseCardRemove}
                    />
                </>
            );

        }}
        </StockCardComponent>
    );
};

export default UpdateWarehouseCardForm;
